import { Panel, Table, Td, Th, Chip } from "@/components/common/Chrome";

const words = (s) => String(s || "").replace(/_/g, " ");
const OUT = {
  approved: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  rejected: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
  no_quorum: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  tied: "border-violet-500/30 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  declined: "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300",
};

export const AnnualFigures = ({ figures }) => (
  <div className="space-y-4">
    <Panel title="Meetings held" testId="figures-meetings"
           kicker={`${figures.counters.meetings_held} held of ${figures.counters.meetings} called`}>
      {figures.meetings.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">No meeting was called in the year.</p>
      ) : (
      <Table head={<><Th>Reference</Th><Th>Meeting</Th><Th>When</Th><Th>Quorum</Th>
        <Th>Attended</Th><Th>Status</Th></>}>
        {figures.meetings.map((m) => (
          <tr key={m.meeting_ref} className="border-b border-border/60"
              data-testid={`figure-meeting-${m.meeting_ref}`}>
            <Td className="font-mono text-[11px]">{m.meeting_ref}</Td>
            <Td>
              <div className="font-medium text-foreground">{m.title}</div>
              <div className="text-[11px] text-muted-foreground">{m.type_label}</div>
            </Td>
            <Td className="font-mono text-[11px]">{m.starts_at.slice(0, 10)}</Td>
            <Td className="font-mono text-[11px]">{m.quorum}</Td>
            <Td className="font-mono text-[11px]">
              {m.attended}
              {m.quorum_met === false && (
                <span className="ml-1 text-red-600 dark:text-red-400">quorum not met</span>
              )}
            </Td>
            <Td>{words(m.status)}</Td>
          </tr>
        ))}
      </Table>
      )}
    </Panel>

    <Panel title="Resolutions decided" testId="figures-resolutions"
           kicker={`${figures.counters.resolutions_carried} carried, ${figures.counters.resolutions_lost} lost`}>
      {figures.resolutions.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">
          The board decided no resolution in the year.
        </p>
      ) : (
      <Table head={<><Th>Reference</Th><Th>Resolution</Th><Th>Decided</Th><Th>Outcome</Th>
        <Th>For / against / abstain</Th><Th>Majority rule</Th></>}>
        {figures.resolutions.map((r) => (
          <tr key={r.item_ref} className="border-b border-border/60"
              data-testid={`figure-resolution-${r.resolution_ref || r.item_ref}`}>
            <Td className="font-mono text-[11px]">{r.resolution_ref || r.item_ref}</Td>
            <Td className="font-medium text-foreground">{r.title}</Td>
            <Td className="font-mono text-[11px]">{r.decided_at}</Td>
            <Td><Chip className={OUT[r.status]}>{words(r.status)}</Chip></Td>
            <Td className="font-mono text-[11px]">
              {r.for}/{r.against}/{r.abstain} of {r.eligible}
            </Td>
            <Td className="text-[11.5px]">{r.majority_label}</Td>
          </tr>
        ))}
      </Table>
      )}
    </Panel>

    <Panel title="Elections declared" testId="figures-elections"
           kicker={`${figures.counters.posts_filled} post(s) filled · average turnout ${figures.counters.average_turnout}%`}>
      {figures.elections.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">No election was declared in the year.</p>
      ) : figures.elections.map((e) => (
        <div key={e.election_ref} className="mb-3 last:mb-0"
             data-testid={`figure-election-${e.election_ref}`}>
          <p className="text-[12.5px] font-medium text-foreground">{e.name}</p>
          <p className="text-[11px] text-muted-foreground">
            {e.results_ref} · term {e.term} · declared {e.declared_at} · turnout{" "}
            {e.turnout?.percent}% ({e.turnout?.cast} of {e.turnout?.roll}) · certified by{" "}
            {e.certified_by}
          </p>
          <div className="mt-1.5 space-y-0.5">
            {e.posts.map((p) => (
              <p key={p.title} className="text-[11.5px] text-muted-foreground">
                <span className="text-foreground">{p.title}</span>:{" "}
                {p.elected.join(", ") || "no result"} · {p.method_label}
              </p>
            ))}
            {e.questions.map((q) => (
              <p key={q.question} className="text-[11.5px] text-muted-foreground">
                {q.question} — {q.answer}
              </p>
            ))}
          </div>
        </div>
      ))}
    </Panel>

    <Panel title="Who held office during the year" testId="figures-holders"
           kicker={`${figures.counters.office_holders} term(s) overlapping the year`}>
      {figures.office_holders.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">
          No elected or appointed term overlaps this year on the register.
        </p>
      ) : (
      <Table head={<><Th>Post</Th><Th>Holder</Th><Th>Term</Th><Th>Route</Th><Th>In a row</Th>
        <Th>Authority</Th></>}>
        {figures.office_holders.map((h) => (
          <tr key={`${h.post_title}-${h.name}-${h.term_start}`}
              className="border-b border-border/60">
            <Td>{h.post_title}</Td>
            <Td className="font-medium text-foreground">{h.name}</Td>
            <Td className="font-mono text-[11px]">{h.term_start} to {h.term_end}</Td>
            <Td>{h.route}</Td>
            <Td className="font-mono text-[11px]">{h.consecutive_terms}</Td>
            <Td className="font-mono text-[11px]">{h.authority}</Td>
          </tr>
        ))}
      </Table>
      )}
    </Panel>

    <Panel title="Interests declared and how they were handled" testId="figures-conflicts"
           kicker={`${figures.counters.conflicts} declaration(s) in the year`}>
      {figures.conflicts.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">
          Nobody declared an interest in the year, which is itself worth testing.
        </p>
      ) : (
      <Table head={<><Th>Reference</Th><Th>Who</Th><Th>Interest</Th><Th>Related party</Th>
        <Th>Declared</Th><Th>Status</Th></>}>
        {figures.conflicts.map((c) => (
          <tr key={c.declaration_ref} className="border-b border-border/60"
              data-testid={`figure-conflict-${c.declaration_ref}`}>
            <Td className="font-mono text-[11px]">{c.declaration_ref}</Td>
            <Td className="font-medium text-foreground">{c.person_name}</Td>
            <Td>{words(c.interest_type)}</Td>
            <Td>{c.related_party}</Td>
            <Td className="font-mono text-[11px]">{c.declared_on}</Td>
            <Td>{words(c.status)}</Td>
          </tr>
        ))}
      </Table>
      )}
    </Panel>

    <Panel title="Decisions above the delegated limit" testId="figures-approvals"
           kicker={`${figures.counters.approvals} decision(s) went to the office bearers or the board`}>
      {figures.approvals.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">
          Nothing above the delegated limit was decided in the year.
        </p>
      ) : (
      <Table head={<><Th>Reference</Th><Th>Subject</Th><Th>Amount</Th><Th>Slab</Th><Th>Outcome</Th>
        <Th>Decided</Th></>}>
        {figures.approvals.map((a) => (
          <tr key={a.request_ref} className="border-b border-border/60"
              data-testid={`figure-approval-${a.request_ref}`}>
            <Td className="font-mono text-[11px]">{a.request_ref}</Td>
            <Td className="font-medium text-foreground">{a.subject}</Td>
            <Td className="font-mono text-[11px]">{Number(a.amount).toLocaleString()}</Td>
            <Td className="font-mono text-[11px]">{a.slab}</Td>
            <Td><Chip className={OUT[a.status]}>{words(a.status)}</Chip></Td>
            <Td className="font-mono text-[11px]">{a.decided_at}</Td>
          </tr>
        ))}
      </Table>
      )}
    </Panel>

    <Panel title="Changes to the governance rulebook" testId="figures-rules"
           kicker={`${figures.counters.rule_changes} version(s) adopted in the year`}>
      {figures.rule_changes.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground">
          The rulebook was not changed during the year.
        </p>
      ) : (
        <div className="space-y-1">
          {figures.rule_changes.map((r) => (
            <p key={r.version} className="text-[11.5px] text-muted-foreground"
               data-testid={`figure-rule-v${r.version}`}>
              <span className="text-foreground">v{r.version}</span> · {r.created_at} ·{" "}
              {r.created_by} — {r.note || "no note"}
            </p>
          ))}
        </div>
      )}
    </Panel>
  </div>
);
